"use client";

/**
 * Pause-enrollment confirmation (Phase J · Task 57).
 *
 * Mirrors the sequence-detail `PauseModal` layout (spec §10.4) and
 * reuses our `Modal`/`ModalHeader`/`ModalBody`/`ModalFooter` shell so
 * the visual treatment matches the rest of the dashboard. Adds an
 * optional `auto_resume_at` datetime-local picker which the workflow
 * uses to schedule a `resume` signal at the chosen time.
 *
 * Uses the native `<input type="datetime-local">` for the picker —
 * the value is converted to an ISO string with timezone before
 * forwarding to the API. We don't pull in a heavyweight calendar
 * widget for this single use case.
 */

import { useState } from "react";

import { Button, SubmitButton } from "@/components/ui/button";
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@/components/ui/modal";
import { showToast } from "@/components/ui/sonner";
import { getApiErrorMessage } from "@/lib/api/error";

import { useEnrollmentsList } from "../../hooks/use-enrollments-list";
import type { EnrollmentListItem } from "../../lib/types";

interface PauseModalProps {
  enrollment: EnrollmentListItem | null;
  jobId: string;
  sequenceId: string;
  open: boolean;
  onOpenChange: (next: boolean) => void;
}

export function PauseEnrollmentModal(props: PauseModalProps) {
  if (!props.open) return null;
  return <PauseEnrollmentModalBody {...props} />;
}

/**
 * Inner body remounts on every open so the local form state
 * (`autoResumeAt`) is reset without leaning on `useEffect` cleanup.
 * Reset-on-mount keeps lint happy (no setState-in-effect) and avoids
 * the Strict-Mode double-mount issue with effect-driven resets.
 */
function PauseEnrollmentModalBody({
  enrollment,
  jobId,
  sequenceId,
  open,
  onOpenChange,
}: PauseModalProps) {
  const { pause } = useEnrollmentsList({ jobId, sequenceId });
  const [autoResumeAt, setAutoResumeAt] = useState<string>("");

  const close = () => {
    if (pause.isPending) return;
    onOpenChange(false);
  };

  const handleConfirm = async () => {
    if (!enrollment) return;
    try {
      const data: { auto_resume_at?: string } = {};
      if (autoResumeAt) {
        const iso = new Date(autoResumeAt).toISOString();
        data.auto_resume_at = iso;
      }
      await pause.mutateAsync({
        jobId,
        sequenceId,
        enrollmentId: enrollment.id,
        data,
      });
      showToast.success("Enrollment paused");
      onOpenChange(false);
    } catch (err) {
      showToast.error("Couldn't pause the enrollment. Please try again.", {
        description: getApiErrorMessage(err),
      });
    }
  };

  return (
    <Modal
      open={open}
      onOpenChange={(next) => (!next ? close() : undefined)}
      dismissible={!pause.isPending}
    >
      <ModalContent size="md">
        <ModalHeader title="Pause enrollment?" iconName="IconPause" />
        <ModalBody>
          <div className="flex flex-col gap-4">
            <p className="text-sm text-muted-foreground">
              The workflow will hold at the current step. No messages will be
              sent and no tasks will be created until you resume.
            </p>
            <div className="flex flex-col gap-1.5">
              <label
                htmlFor="enrollment-pause-auto-resume"
                className="text-sm font-medium text-heading"
              >
                Auto-resume at{" "}
                <span className="font-normal text-muted-foreground">
                  (optional)
                </span>
              </label>
              <input
                id="enrollment-pause-auto-resume"
                data-testid="enrollment-pause-auto-resume"
                type="datetime-local"
                value={autoResumeAt}
                onChange={(event) => setAutoResumeAt(event.target.value)}
                disabled={pause.isPending}
                className="h-9 rounded-md border border-border bg-white/4 px-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring/40"
              />
              <p className="text-xs text-muted-foreground">
                Leave empty to require a manual resume.
              </p>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            variant="secondary"
            size="sm"
            onClick={close}
            disabled={pause.isPending}
          >
            Cancel
          </Button>
          <SubmitButton
            type="button"
            variant="primary"
            size="sm"
            loading={pause.isPending}
            loadingLabel="Pausing"
            onClick={handleConfirm}
          >
            Pause
          </SubmitButton>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
